import { Formik } from 'formik'
import React, { useEffect, useRef, useState } from 'react'
import { Modal, Form } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import TextInput from '../../widgets/components/Input/TextInput'
import { BasicButton } from '../../widgets/components/UI/BasicButton'
import { ToolTipUIForModal } from '../../widgets/components/UI/ToolTipUIForModal'
import { useAuth } from '../../auth'
import { addUser, editUser, getUser } from '../core/_requests'
import { toast } from 'react-toastify'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../hooks/gtmHelpers'
import * as Yup from 'yup'
import { roleValues } from '../../../core/_constants'

const AddTeamMemberModal = ({
  showModal,
  setShowModal,
  allCompanyRoles,
  memberId,
  getAllCompanyRoles,
  getUsersList,
  setMemberId,
  setSelectedSidebarId,
}: {
  showModal: boolean
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>
  allCompanyRoles: any[]
  memberId: number | null
  setMemberId: React.Dispatch<React.SetStateAction<number | null>>
  getAllCompanyRoles: () => void | Promise<void>
  getUsersList: () => void | Promise<void>
  setSelectedSidebarId: React.Dispatch<React.SetStateAction<number>>
}) => {
  const { formatMessage } = useIntl()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [loading, setLoading] = useState(false) //eslint-disable-line
  const [searchRole, setSearchRole] = useState(false)
  const [selectRole, setSelectRole] = useState<{ roleId: string; roleName: string } | null>(null)
  const [searchText, setSearchText] = useState('')
  const [filterAllCompanyRoles, setFilterAllCompanyRoles] = useState<any[]>([])
  const { companyId, currentUser } = useAuth()
  const [userDetails, setUserDetails] = useState<any>()
  const initialValue = {
    firstname: userDetails?.firstName || '',
    lastname: userDetails?.lastName || '',
    email: userDetails?.email || '',
    role: userDetails?.userRole?.roleName || '',
  }
  const userSchema = Yup.object().shape({
    firstname: Yup.string()
      .min(1, formatMessage({ id: 'Minimum 1 character is required' }))
      .max(50, formatMessage({ id: 'Maximum 50 characters' }))
      .required(formatMessage({ id: 'Full name is required' }))
      .nullable(),
    lastname: Yup.string()
      .min(1, formatMessage({ id: 'Minimum 1 character is required' }))
      .max(50, formatMessage({ id: 'Maximum 50 characters' }))
      .required(formatMessage({ id: 'Last name is required' }))
      .nullable(),
    email: Yup.string()
      .email(formatMessage({ id: 'Invalid email format' }))
      .max(50, formatMessage({ id: 'Maximum 50 characters' }))
      .required(formatMessage({ id: 'Email is required' })),
    role: Yup.string()
      .required(formatMessage({ id: 'Role is required' }))
      .nullable(),
  })

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true)
      if (memberId) {
        const payload = {
          companyId: companyId,
          firstName: values.firstname,
          lastName: values.lastname,
          email: values.email,
          roleId: selectRole?.roleId,
        }
        const {
          data: { success, errors },
        } = await editUser(userDetails?.userId, payload)
        if (success) {
          setLoading(false)
          toast.success(formatMessage({ id: 'User Updated' }))
          getUsersList()
          setMemberId(null)
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.EditUserSuccessful,
              action: 'normal',
              metadata: {
                company_id: companyId,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                invited_user_first_name: values.firstname,
                invited_user_last_name: values.lastname,
                invited_user_email: values.email,
                invited_user_designation: values.designation,
                invited_user_role_id: [values.roles],
                invited_user_id: userDetails?.userId,
              },
            },
          })
        } else {
          setLoading(false)
          errors.forEach((error: string) => {
            toast.error(formatMessage({ id: error }))
          })
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.EditUserFailed,
              action: 'normal',
              metadata: {
                company_id: companyId,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                invited_user_first_name: values.firstname,
                invited_user_last_name: values.lastname,
                invited_user_email: values.email,
                invited_user_designation: values.designation,
                invited_user_role_id: [values.roles],
                invited_user_id: userDetails?.userId,
                error: errors[0],
              },
            },
          })
        }
      } else {
        const payload = {
          companyId: companyId?.toString(),
          Users: [
            {
              firstName: values.firstname,
              lastName: values.lastname,
              email: values.email,
              designation: values.designation,
              roleId: selectRole?.roleId,
            },
          ],
        }
        const {
          data: { success, errors },
        } = await addUser(payload)
        if (success) {
          getAllCompanyRoles()
          getUsersList()
          setMemberId(null)
          toast.success(formatMessage({ id: 'User Invited' }))
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.AddUserSuccessful,
              action: 'normal',
              metadata: {
                company_id: companyId,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                invited_user_first_name: values.firstname,
                invited_user_last_name: values.lastname,
                invited_user_email: values.email,
                invited_user_designation: values.designation,
                invited_user_role_id: [values.roles],
              },
            },
          })
        } else {
          errors.forEach((error: string) => {
            toast.error(formatMessage({ id: error }))
          })
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.AddUserFailed,
              action: 'normal',
              metadata: {
                company_id: companyId,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                invited_user_first_name: values.firstname,
                invited_user_last_name: values.lastname,
                invited_user_email: values.email,
                invited_user_designation: values.designation,
                invited_user_role_id: [values.roles],
                error: errors[0],
              },
            },
          })
        }
      }
    } catch (err) {
      console.log(err)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.EditUserFailed,
          action: 'normal',
          metadata: {
            company_id: companyId,
            user_id: currentUser?.userId,
            email: currentUser?.email,
            invited_user_first_name: values.firstname,
            invited_user_last_name: values.lastname,
            invited_user_email: values.email,
            invited_user_designation: values.designation,
            invited_user_role_id: [values.roles],
            // invited_user_id: userDetails?.userId,
            error: err,
          },
        },
      })
    } finally {
      setLoading(false)
      setShowModal(false)
    }
  }
  useEffect(() => {
    setFilterAllCompanyRoles(
      allCompanyRoles.filter((role: any) =>
        role.roleName.toLowerCase().includes(searchText.toLowerCase())
      )
    )
  }, [allCompanyRoles, searchText, searchRole])

  const getUserData = async () => {
    try {
      const {
        data: { success, data, errors },
      } = await getUser(companyId, +memberId!)
      if (success) {
        setUserDetails(data)
      } else {
        errors.forEach((error: string) => {
          toast.error(formatMessage({ id: error }))
        })
      }
    } catch (err) {
      console.log(err)
    }
  }
  useEffect(() => {
    if (memberId) {
      getUserData()
    }
  }, [memberId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      const dropdownElement = document.getElementById('panelsStayOpen-collapseSix')
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && dropdownElement) {
        dropdownElement.classList.remove('show')
      }
    }

    document.addEventListener('click', handleClickOutside)

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  const handleAddNewRole = () => {
    setSelectedSidebarId(2)
  }

  const hexToRgba = (hex: string, opacity: number): string => {
    const sanitizedHex = hex.replace('#', '');

    const bigint = parseInt(sanitizedHex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  return (
    <>
      <div>
        <Modal
          show={showModal}
          onHide={() => {
            setShowModal(false)
            setMemberId(null)
          }}
          aria-labelledby='contained-modal-title-vcenter'
          centered
        >
          <Modal.Header closeButton>
            <h1 className='mb-0 text-center font-size-20'>
              {formatMessage({
                id: memberId ? 'Update Team Member' : 'Add Team Member',
              })}
            </h1>{' '}
          </Modal.Header>
          <div>
            <div
              className='d-flex align-items-center justify-content-center h-100'
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <div className='card d-flex justify-content-center align-items-center otp-screen py-6 px-md-0 px-2 gap-6 w-100'>
                <Formik
                  initialValues={initialValue}
                  validationSchema={userSchema}
                  onSubmit={handleSubmit}
                >
                  {(formik) => (
                    <Form className='w-100 row container'>
                      <div className='col-md-6 col-12'>
                        <TextInput
                          fieldType='text'
                          label={formatMessage({ id: 'First name' })}
                          fieldName='firstname'
                          formik={formik}
                          placeholder={formatMessage({ id: 'Enter First name' })}
                          toolTipText={formatMessage({
                            id: `Provide the team member's first name as it will appear in the system. This helps identify users across the platform.`,
                          })}
                          isStarRequired={true}
                          isTooltipForModal={true}
                          onBlur={formik.handleBlur}
                        />
                      </div>
                      <div className='col-md-6 col-12'>
                        <TextInput
                          fieldType='text'
                          label={formatMessage({ id: 'Last name' })}
                          fieldName='lastname'
                          formik={formik}
                          placeholder={formatMessage({ id: 'Enter Last name' })}
                          toolTipText={formatMessage({
                            id: `Enter the team member's last name for proper identification and sorting in team lists and audit trails.`,
                          })}
                          isStarRequired={true}
                          isTooltipForModal={true}
                          onBlur={formik.handleBlur}
                        />
                      </div>
                      <div className='col-md-12 col-12'>
                        <TextInput
                          fieldType='email'
                          label={formatMessage({ id: 'Email Id' })}
                          fieldName='email'
                          formik={formik}
                          placeholder={formatMessage({ id: 'Enter Email Id' })}
                          toolTipText={formatMessage({
                            id: `Input the team member's business email which serves as their unique identifier and login credential for accessing RecordsKeeper.AI.`,
                          })}
                          isStarRequired={true}
                          isTooltipForModal={true}
                          onBlur={formik.handleBlur}
                        />
                      </div>
                      <div className='col-md-12 col-12'>
                        {/* <div className=''>
                          <span className='d-flex align-items-center'>
                            <p className='mb-2'>
                              {formatMessage({id: 'Assign Role'})}
                              <ToolTipUIForModal tooltipText='Select appropriate permissions for this team member. Different roles control what records they can access, edit, or view to maintain security and compliance.' />
                            </p>
                          </span>
                          <div
                            className='d-flex align-items-center mb-8 form-control h-40px font-size-12'
                            onClick={() => handlesearchRole()}
                            style={mode === 'dark' ? {background: '#181c32'} : {}}
                          >
                            <span className='d-flex justify-content-between align-items-center w-100'>
                              <p
                                className={`mb-0 ${
                                  selectRole?.roleName !== '' ? '' : 'text-muted'
                                }`}
                              >
                                {formatMessage({id: selectRole?.roleName || 'Search Role'})}
                              </p>
                              <i className='fa-solid fa-angle-down'></i>
                            </span>
                          </div>
                          <div
                            className={`bg-white d-flex flex-column gap-4 border my-2 p-2 rounded ${
                              !searchRole ? 'd-none' : ''
                            }`}
                          >
                            <div>
                              <form
                                className='bg-white d-flex align-items-center w-100 w-md-auto border border-2 rounded'
                                // style={{ background: '#181c32' }}
                              >
                                <span className='svg-icon svg-icon-3 position-absolute ms-3'>
                                  <svg
                                    width='24'
                                    height='24'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                    className='mh-50px'
                                  >
                                    <rect
                                      opacity='0.5'
                                      x='17.0365'
                                      y='15.1223'
                                      width='8.15546'
                                      height='2'
                                      rx='1'
                                      transform='rotate(45 17.0365 15.1223)'
                                      fill='currentColor'
                                    ></rect>
                                    <path
                                      d='M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z'
                                      fill='currentColor'
                                    ></path>
                                  </svg>
                                </span>
                                <input
                                  value={searchText}
                                  onChange={(e) => setSearchText(e.target.value)}
                                  className='bg-body form-control form-control-flush ps-10 rounded-2 font-size-13 font-weight-400 height-36'
                                  placeholder='Search Role'
                                />
                              </form>
                            </div>
                            {filterAllCompanyRoles.length > 0 ? (
                              filterAllCompanyRoles.map((roles) => {
                                if (roles.roleName.toLowerCase() === roleValues.OWNER) return null
                                return (
                                  <>
                                    <div
                                      key={roles.roleId}
                                      className={`d-flex justify-content-between align-items-center p-4 ${
                                        selectRole?.roleName === roles.roleName &&
                                        `bg-${roles.tagColor} bg-opacity-10 rounded`
                                      }`}
                                      onClick={() => {
                                        setSelectRole({
                                          roleId: roles.roleId,
                                          roleName: roles.roleName,
                                        })
                                        setSearchRole(false)
                                        setSearchText('')
                                      }}
                                    >
                                      <div className='d-flex flex-column align-items-start'>
                                        <span
                                          className={`mb-0 bg-${roles.tagColor} bg-opacity-20 text-${roles.tagColor} p-2 rounded`}
                                        >
                                          {formatMessage({id: roles.roleName})}
                                        </span>
                                        <p className='mb-0'>
                                          {formatMessage({
                                            id: roles.roleDescription,
                                          })}
                                        </p>
                                      </div>
                                      <i
                                        className={`fa-solid fa-check text-primary ${
                                          selectRole?.roleName === roles.roleName ? '' : 'd-none'
                                        }`}
                                      ></i>
                                    </div>
                                  </>
                                )
                              })
                            ) : (
                              <p className='text-muted text-center'>No roles found</p>
                            )}
                            <button
                              className='btn bg-primary text-white'
                              onClick={handleAddNewRole}
                              type='button'
                            >
                              {formatMessage({id: '+ Add New Role'})}
                            </button>
                          </div>
                        </div> */}
                        <div className=''>
                          <span className='d-flex align-items-center'>
                            <p className='mb-2'>
                              {formatMessage({ id: 'Assign Role' })}
                              <ToolTipUIForModal tooltipText='Select appropriate permissions for this team member. Different roles control what records they can access, edit, or view to maintain security and compliance.' />
                            </p>
                          </span>
                          <div className='accordion-item mb-8'
                            ref={dropdownRef}
                          >
                            <h2 className='accordion-header rounded' id='panelsStayOpen-headingSix'>
                              <button
                                className='accordion-button collapsed w-100 app-sidebar-logo-default custom-accordion-button'
                                type='button'
                                data-bs-toggle='collapse'
                                data-bs-target='#panelsStayOpen-collapseSix'
                                aria-expanded='false'
                                aria-controls='panelsStayOpen-collapseSix'
                              >
                                <div className='d-flex  justify-content-between align-items-center w-100 form form-control'>
                                  <p
                                    className={`mb-0 w-100 ${selectRole?.roleName !== '' ? '' : 'text-muted'
                                      }`}
                                  >
                                    {formatMessage({ id: selectRole?.roleName || 'Search Role' })}
                                  </p>
                                  <i
                                    className='align-self-right fa fa-angle-down ps-2 text-clr477 text-primary'
                                    aria-hidden='true'
                                  ></i>
                                </div>
                              </button>
                            </h2>
                            <div className='w-100 app-sidebar-logo-minimize d-flex rounded-2'>
                            </div>
                            <div
                              id='panelsStayOpen-collapseSix'
                              className='accordion-collapse p-2 bg-white collapse w-100 shadow border-bottom mh-1000px rounded-2 position-absolute'
                              style={{ zIndex: '99' }}
                              aria-labelledby='panelsStayOpen-headingSix'
                            >
                              <div className='accordion-body app-sidebar-logo-default mh-550px bg-white'>
                                <div className='border border-1 rounded-3 mb-2'>
                                  <form
                                    className='bg-white d-flex align-items-center w-100 w-md-auto border rounded'
                                  >
                                    <span className='svg-icon svg-icon-3 position-absolute ms-3'>
                                      <svg
                                        width='24'
                                        height='24'
                                        viewBox='0 0 24 24'
                                        fill='none'
                                        xmlns='http://www.w3.org/2000/svg'
                                        className='mh-50px'
                                      >
                                        <rect
                                          opacity='0.5'
                                          x='17.0365'
                                          y='15.1223'
                                          width='8.15546'
                                          height='2'
                                          rx='1'
                                          transform='rotate(45 17.0365 15.1223)'
                                          fill='currentColor'
                                        ></rect>
                                        <path
                                          d='M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z'
                                          fill='currentColor'
                                        ></path>
                                      </svg>
                                    </span>
                                    <input
                                      value={searchText}
                                      onChange={(e) => setSearchText(e.target.value)}
                                      className='bg-body form-control form-control-flush ps-10 rounded-2 font-size-13 font-weight-400 height-36'
                                      placeholder='Search Role'
                                    />
                                  </form>
                                </div>
                                <div className='d-flex flex-column gap-2 mb-4'>
                                  {filterAllCompanyRoles.length > 0 ? (
                                    filterAllCompanyRoles.map((roles) => {
                                      if (roles.roleName.toLowerCase() === roleValues.OWNER) return null
                                      return (
                                        <>
                                          {/* <div
                                            key={roles.roleId}
                                            className={`d-flex justify-content-between align-items-center p-4 ${selectRole?.roleName === roles.roleName &&
                                              ` bg-opacity-10 rounded`
                                              }`} style={selectRole?.roleName === roles.roleName ? { background: roles.tagColor } : {}}
                                            onClick={() => {
                                              setSelectRole({
                                                roleId: roles.roleId,
                                                roleName: roles.roleName,
                                              })
                                              setSearchRole(false)
                                              setSearchText('')
                                            }}
                                          > */}
                                          <div
                                            key={roles.roleId}
                                            className={`d-flex justify-content-between align-items-center p-4 rounded`}
                                            style={selectRole?.roleName === roles.roleName ? {
                                              background: hexToRgba(roles.tagColor, 0.1),
                                            } : {}}
                                            onClick={() => {
                                              setSelectRole({
                                                roleId: roles.roleId,
                                                roleName: roles.roleName,
                                              });
                                              setSearchRole(false);
                                              setSearchText('');
                                            }}
                                          >
                                            {/* <div className='d-flex flex-column align-items-start'>
                                              <span
                                                className={`mb-0  bg-opacity-20 p-2 rounded`}
                                                style={{ color: roles.tagColor, background: roles.tagColor }}
                                              >
                                                {formatMessage({ id: roles.roleName })}
                                              </span>
                                              <p className='mb-0'>
                                                {formatMessage({
                                                  id: roles.roleDescription,
                                                })}
                                              </p>
                                            </div> */}
                                            <div className='d-flex flex-column align-items-start'>
                                              <span
                                                className='mb-0 p-2 rounded'
                                                style={{
                                                  color: roles.tagColor,
                                                  background: hexToRgba(roles.tagColor, 0.2),
                                                }}
                                              >
                                                {formatMessage({ id: roles.roleName })}
                                              </span>
                                              <p className='mb-0'>
                                                {formatMessage({ id: roles.roleDescription })}
                                              </p>
                                            </div>
                                            <i
                                              className={`fa-solid fa-check text-primary ${selectRole?.roleName === roles.roleName ? '' : 'd-none'
                                                }`}
                                            ></i>
                                          </div>
                                        </>
                                      )
                                    })
                                  ) : (
                                    <p className='text-muted text-center'>{formatMessage({ id: 'No roles found' })}</p>
                                  )}
                                </div>
                                <button
                                  className='btn bg-primary text-white custom-button-property w-100'
                                  onClick={handleAddNewRole}
                                  type='button'
                                >
                                  {formatMessage({ id: '+ Add New Role' })}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='d-flex flex-md-row flex-column justify-content-end gap-2'>
                        <BasicButton
                          buttonText={formatMessage({ id: 'Cancel' })}
                          height='44px'
                          customClass='bg-light text-muted order-2 order-md-1'
                          // padding='12px 24px'
                          loading={loading}
                          onClick={() => {
                            setShowModal(false)
                            setMemberId(null)
                          }}
                        />
                        <BasicButton
                          buttonText={formatMessage({ id: memberId ? 'Update User' : 'Add User' })}
                          height='44px'
                          customClass='bg-primary text-white order-1 order-md-2'
                          // padding='12px 24px'
                          loading={loading}
                          onClick={() => handleSubmit(formik.values)}
                        />
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    </>
  )
}

export default AddTeamMemberModal
